<template>
  <div>
    <div class="mt-2 flex w-full flex-col gap-y-2">
      <div
        v-if="fileUploadError"
        class="relative w-full rounded-lg border border-red-600 px-4 py-6"
      >
        <p>
          Error: <span class="text-red-700">{{ fileErrorMessage }}</span>
        </p>
      </div>

      <div v-if="uploadedFiles.length">
        <div
          v-for="(file, fIndex) in uploadedFiles"
          :key="`file-${fIndex}`"
        >
          <div
            v-if="file"
            class="relative mb-2 flex w-full items-center justify-between gap-x-2 rounded-lg border border-[#218579] bg-[#F2F6F8] px-4 py-2"
          >
            <span
              class="line-clamp-1 w-5/6 whitespace-nowrap rounded-sm py-2 text-sm font-medium text-[#344054]"
            >
              {{ file.document_name_without_id }}
            </span>
            <div class="flex w-1/3 justify-end gap-x-2">
              <span class="mt-0 text-sm text-[#1A242A8C]">{{
                Number((file.file_size / 1024).toFixed(2)) > 1024
                  ? (file.file_size / 1024 / 1024).toFixed(2) + "MB"
                  : (file.file_size / 1024).toFixed(2) + "KB"
              }}</span>
              <button
                aria-label="Delete file"
                class="outline-none"
                type="button"
                @click="deleteUploadedFile(file.document_name)"
              >
                <TrashIcon
                  :custom-style="`text-[#E95844] h-5 w-5 rounded-full`"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="attachedDocuments.length">
        <div
          v-for="(file, fIndex) in attachedDocuments"
          :key="`file-${fIndex}`"
        >
          <div
            v-if="file"
            class="relative mb-2 flex w-full items-center justify-between gap-x-2 rounded-lg border border-[#218579] bg-[#F2F6F8] px-4 py-2"
          >
            <span
              class="line-clamp-1 w-2/3 whitespace-nowrap rounded-sm py-2 text-sm font-medium text-[#344054]"
            >
              {{ file.name }}
            </span>
            <div class="flex w-1/3 justify-end gap-x-2">
              <span class="mt-0 text-sm text-[#1A242A8C]">{{
                Number((file.size / 1024).toFixed(2)) > 1024
                  ? (file.size / 1024 / 1024).toFixed(2) + "MB"
                  : (file.size / 1024).toFixed(2) + "KB"
              }}</span>
              <button
                aria-label="Delete file"
                class="outline-none"
                type="button"
                @click="clearFormUpload(fIndex)"
              >
                <TrashIcon
                  :custom-style="`text-[#E95844] h-5 w-5 rounded-full`"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        class="relative h-auto w-full rounded border-2 border-dashed border-gray-200 bg-main-primary bg-opacity-[0.03] p-10"
        @drop.prevent="onDrop"
        @dragenter.prevent="
          removeContainerActivity = false;
          dropActive = true;
        "
        @dragleave.prevent="dropActive = false"
        @dragover.prevent="
          removeContainerActivity = false;
          dropActive = true;
        "
      >
        <div>
          <div class="flex flex-col items-center justify-center">
            <div class="flex items-start gap-2">
              <span class="text-md whitespace-nowrap font-medium text-slate-900">Drag & drop file here
              </span>
              <button
                class="whitespace-nowrap font-bold text-[#1A242A82]"
                @click="triggerInput"
              >
                or Browse
              </button>
            </div>
            <input
              ref="fileInput"
              type="file"
              class="hidden"
              accept=".pdf, .png, .jpg, .doc, .docx, .jpeg, .xls, .csv, .xlsx"
              @change="receiveFileFromInput"
            >
          </div>
          <p class="text-center text-sm text-[#1A242A82]">
            Supported formats: csv, xls, png, jpeg, pdf, docx
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DataEntryDocument',
  props: {
    fileList: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['deleteDocument', 'documentAttached'],

  data() {
    return {
      events: ['dragenter', 'dragleave', 'dragover', 'drop'],
      dropActive: false,
      fileUploadError: false,
      removeContainerActivity: false,
      uploadedFiles: this.fileList,
      fileErrorMessage: null,
      attachedDocuments: [],
    }
  },

  computed: {
    containerActive() {
      return this.dropActive && !this.removeContainerActivity
    },
  },

  watch: {
    fileList(newFiles) {
      this.uploadedFiles = newFiles
    },
  },

  mounted() {
    this.events.forEach(eventName =>
      document.body.addEventListener(eventName, this.preventDefaults),
    )
  },

  beforeUnmount() {
    this.events.forEach(eventName =>
      document.body.removeEventListener(eventName, this.preventDefaults),
    )
  },

  methods: {
    onDrop(event) {
      this.dropActive = true
      const files = [...event.dataTransfer.files]
      this.checkAttachedFileType(files)
      if (!this.fileUploadError) {
        this.attachedDocuments.push(files[0])
        this.$emit('documentAttached', this.attachedDocuments)
      }
    },
    preventDefaults(event) {
      event.preventDefault()
    },
    triggerInput() {
      this.removeContainerActivity = false
      this.$refs.fileInput.click()
    },
    receiveFileFromInput(event) {
      const files = [...event.target.files]
      if (files.length) {
        this.checkAttachedFileType(files)
        if (!this.fileUploadError) {
          this.attachedDocuments.push(files[0])
          this.$emit('documentAttached', this.attachedDocuments)
        }
      }
    },
    clearFormUpload(index) {
      this.attachedDocuments.splice(index, 1)
      this.$emit('documentAttached', this.attachedDocuments)
      getCurrentInstance()?.proxy?.$forceUpdate()
    },
    checkAttachedFileType(file) {
      this.fileErrorMessage = null
      this.fileUploadError = false
      if (Number(file[0].size / 1024 / 1024) >= 20) {
        this.fileUploadError = true
        this.fileErrorMessage
          = 'The file you are trying to upload exceeds our maximum file size limit of 20 MB. Please try again.'
        return
      }
      if (
        file.length
        && ![
          'xls',
          'xlsx',
          'csv',
          'pdf',
          'doc',
          'docx',
          'png',
          'jpg',
          'jpeg',
        ].includes(file[0].name.split('.').pop())
      ) {
        this.fileUploadError = true
        this.fileErrorMessage
          = 'Sorry, this file type is not supported, please upload a file of type .xls, .xlsx, .docx, .png, .jpg or pdf'
      }
    },
    deleteUploadedFile(filename) {
      this.$emit('deleteDocument', filename)
    },
  },
}
</script>
